<template>
  <v-container fluid class="accessibility-control-wrapper py-0">
    <div class="main-container">
      <v-layout justify-start align-center class="accessibility-control">
        <div class="accessibility-item">
          <p class="bold">{{$t('accessible.options')}}</p>
        </div>
        <div class="accessibility-item">
          <language-menu bg-col="rgba(255,255,255,100)"/>
        </div>
        <div class="accessibility-item">
          <label for="text-size-selector">{{$t('accessible.text_size')}}</label>
          <input type="hidden" id="text-size-selector" name="text-size-selector" />
          <div class="text-size-selectors">
            <button
              @click="onTextSizeChanged"
              class="small focusable"
              :class="selectedTextSize === 'small-text' && 'primary primaryText--text'"
              role="button"
              :aria-label="$t('aria_label_text_selector_small')"
              :aria-pressed="selectedTextSize === 'small-text' ? 'true' : 'false'"
              :aria-disabled="selectedTextSize === 'small-text' ? 'true' : 'false'">A</button>
            <button
              @click="onTextSizeChanged"
              class="medium focusable"
              :class="selectedTextSize === 'medium-text' && 'primary primaryText--text'"
              role="button"
              :aria-label="$t('aria_label_text_selector_medium')"
              :aria-pressed="selectedTextSize === 'medium-text' ? 'true' : 'false'"
              :aria-disabled="selectedTextSize === 'medium-text' ? 'true' : 'false'">A</button>
            <button
              @click="onTextSizeChanged"
              class="large focusable"
              :class="selectedTextSize === 'large-text' && 'primary primaryText--text'"
              role="button"
              :aria-label="$t('aria_label_text_selector_large')"
              :aria-pressed="selectedTextSize === 'large-text' ? 'true' : 'false'"
              :aria-disabled="selectedTextSize === 'large-text' ? 'true' : 'false'">A</button>
          </div>
        </div>
        <div class="accessibility-item">
          <AccessibilitySwitch/>
        </div>
      </v-layout>
    </div>
  </v-container>
</template>

<script>
import { getCurrentSubThemeName, getCurrentTextSize, setSubTheme } from '@/utils/theme'
import {mapActions, mapGetters} from 'vuex'
import { LOGOUT } from '../../..//store/action-types'
import { SET_PERSONAL_MENU } from '../../..//store/mutation-types'
import LanguageMenu from '../../navbar/LanguageMenu'
import AccessibilitySwitch from '../../navbar/AccessibilitySwitch'

export default {
  name: 'AccessibilityControl',
  components: {AccessibilitySwitch, LanguageMenu},
  data: () => {
    return {
      selectedSubTheme: getCurrentSubThemeName(),
      selectedTextSize: getCurrentTextSize(),
      items: ['Default', 'Protanopia', 'Deuteranopia', 'Tritanopia']
    }
  },
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      getCurrentLanguageDescription: 'getCurrentLanguageDescription',
      getLanguageDescriptions: 'getLanguageDescriptions',
      getFromDesc: 'getLanguageFromDescription'
    })
  },
  methods: {
    ...mapActions({
      doLogout: LOGOUT
    }),
    onTextSizeChanged (e) {
      const size = e.target.classList[0]
      this.$root.$emit('text-size-changed', size)
      this.selectedTextSize = getCurrentTextSize()
    },
    onThemeChanged (e) {
      const selected = e.target.value
      this.$vuetify.theme = setSubTheme(selected)
    },
    languageChanged (description) {
      this.$store.commit(SET_PERSONAL_MENU, false)
      const locale = this.getFromDesc(description)
      this.$i18n.set(locale)
    },
    logout () {
      this.doLogout()
      if (!this.$store.state.config.useSSO && !this.$store.state.config.useSAML) {
        this.$router.push({name: 'Login'})
      } else {
        window.location.replace(this.$store.state.config.authUrlLogOut)
      }
    }
  }
}
</script>

<style scoped>
  p, h1, h2, h3, h4, h5, h6 {
    margin: 0;
    padding: 0;
    line-height: auto;
    font-weight: normal;
  }

  .focusable:focus {
      outline: 3px solid orange !important;
  }

  .main-container {
      max-width: 1420px;
      margin: auto;
  }

  option {
    color: #000 !important;
  }

  .bold {
      font-weight: bold;
  }

  .accessibility-control-wrapper {
    border-bottom: 2px solid #000;
  }

  .accessibility-control {
    padding: 5px 0;
  }

  .accessibility-item {
    display: flex;
    align-items: center;
    margin-right: 20px;
  }

  @media (max-width: 600px) {
    .accessibility-control {
      display: -ms-flexbox;
      display: flex;
      -ms-flex-wrap: wrap;
      flex-wrap: wrap;
      padding: 10px 0;
    }
    .accessibility-item {
      -ms-flex: 0 0 100%;
      flex: 0 0 100%;
      max-width: 100%;
      padding: 5px 0;
    }
  }

  .accessibility-item label {
    margin-right: 10px;
  }

  #colour-blind-selector {
    width: 150px;
    height:auto !important;
    padding: 0 5px;
    border: 1px solid black;
    border-radius: 5px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: url('/static/icons/arrow-down.svg') no-repeat 94% center;
    outline: none !important;
    overflow: auto;
  }

  #colour-blind-selector.dark-selector {
    border-color: white;
    background-image: url('/static/icons/white-arrow-down.svg');
  }

  button {
    cursor: pointer !important;
    margin-right: 2px !important;
    padding: 2px;
    border-radius: 7px;
  }

  button.small {
    font-size: 10px;
  }

  button.medium {
    font-size: 14px;
  }

  button.large {
    font-size: 18px;
  }

  .small-text * {
    font-size: 14px;
  }

  .small-text h1 {
    font-size: 24px;
  }

  .small-text h2 {
    font-size: 17px;
  }

  .small-text h3 {
    font-size: 12.38px;
  }

  .medium-text * {
    font-size: 18px;
  }

  .medium-text h1 {
    font-size: 28px;
  }

  .medium-text h2 {
    font-size: 21px;
  }

  .medium-text h3 {
    font-size: 16.38px;
  }

  .large-text * {
    font-size: 22px;
  }

  .large-text h1 {
    font-size: 32px;
  }

  .large-text h2 {
    font-size: 25px;
  }

  .large-text h3 {
    font-size: 20.38px;
  }
</style>



// WEBPACK FOOTER //
// src/components/student/accessible/AccessibilityControl.vue